<template>
  <div class="modal-overlay" @click.self="closeDialog">
    <div class="add-friend-dialog" :class="{ 'scanner-mode': showScanner }">
      <!-- Main Form View -->
      <template v-if="!showScanner">
        <!-- Header -->
        <div class="dialog-header">
          <div class="header-content">
            <div class="header-icon">
              <i class="bi bi-person-plus-fill"></i>
            </div>
            <h2>{{ $t("chat.add_friend") || "Add Friend" }}</h2>
          </div>
          <button class="close-btn" @click="closeDialog">
            <i class="bi bi-x-lg"></i>
          </button>
        </div>

        <!-- Content -->
        <div class="dialog-content">
          <!-- Shareable ID Input -->
          <div class="form-field">
            <label class="field-label">{{
              $t("chat.shareable_id") || "Shareable ID"
            }}</label>
            <div class="field-row">
              <input
                type="text"
                class="field-input"
                v-model="shareableId"
                :placeholder="
                  $t('chat.paste_shareable_id') || 'Paste shareable ID here'
                "
                :disabled="sending"
              />
              <button
                class="paste-btn"
                @click="pasteFromClipboard"
                :disabled="sending"
                :title="$t('common.paste') || 'Paste'"
              >
                <i :class="pasted ? 'bi bi-check-lg' : 'bi bi-clipboard'"></i>
              </button>
            </div>
          </div>

          <!-- Request Message Input -->
          <div class="form-field">
            <label class="field-label">
              {{ $t("chat.request_message") || "Request Message" }}
              <span class="optional-text"
                >({{ $t("common.optional") || "Optional" }})</span
              >
            </label>
            <textarea
              class="field-textarea"
              v-model="message"
              :placeholder="
                $t('chat.request_message_hint') ||
                'Hi, I\'d like to add you as a friend'
              "
              rows="3"
              :disabled="sending"
            ></textarea>
          </div>

          <!-- Error Message -->
          <div v-if="error" class="message-box error-box">
            <i class="bi bi-exclamation-circle"></i>
            <span>{{ error }}</span>
          </div>

          <!-- Success Message -->
          <div v-if="success" class="message-box success-box">
            <i class="bi bi-check-circle-fill"></i>
            <span>{{
              $t("chat.request_sent_successfully") ||
              "Request sent successfully!"
            }}</span>
          </div>

          <!-- Scan QR Button -->
          <button
            v-if="enableQrScanner"
            class="qr-scan-btn"
            @click="openScanner"
            :disabled="sending"
          >
            <i class="bi bi-qr-code-scan"></i>
            <span>{{ $t("chat.scan_qr_code") || "Scan QR Code" }}</span>
          </button>
        </div>

        <!-- Footer -->
        <div class="dialog-footer">
          <button class="btn-cancel" @click="closeDialog" :disabled="sending">
            {{ $t("common.cancel") || "Cancel" }}
          </button>
          <button
            class="btn-send"
            @click="sendRequest"
            :disabled="!shareableId || sending"
          >
            <i v-if="sending" class="bi bi-arrow-repeat spin"></i>
            <i v-else class="bi bi-send-fill"></i>
            <span>{{
              sending
                ? $t("chat.sending") || "Sending..."
                : $t("chat.send_request") || "Send Request"
            }}</span>
          </button>
        </div>
      </template>

      <!-- QR Scanner View (hidden by default) -->
      <template v-else>
        <div class="scanner-header">
          <button class="back-btn" @click="closeScanner">
            <i class="bi bi-arrow-left"></i>
          </button>
          <h2>{{ $t("chat.scan_qr_code") || "Scan QR Code" }}</h2>
          <div class="spacer"></div>
        </div>

        <div class="scanner-container">
          <div ref="scannerElement" class="scanner-element"></div>
          <div class="scanner-overlay">
            <div class="scanner-frame"></div>
          </div>
          <p class="scanner-instruction">
            {{ $t("chat.scan_instruction") || "Point camera at QR code" }}
          </p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { ChatService } from "@/services/chat";

// Feature flag to enable/disable QR scanner - set to true to show scanner button
const ENABLE_QR_SCANNER = false;

let Html5Qrcode = null;

export default {
  name: "AddFriendDialog",
  emits: ["close", "request-sent"],
  props: {
    initialShareableId: {
      type: String,
      default: "",
    },
    instructorName: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      shareableId: this.initialShareableId || "",
      message: "",
      sending: false,
      error: null,
      success: false,
      showScanner: false,
      html5QrCode: null,
      enableQrScanner: ENABLE_QR_SCANNER,
      idFocused: false,
      msgFocused: false,
      pasted: false,
    };
  },
  watch: {
    initialShareableId(newVal) {
      if (newVal) {
        this.shareableId = newVal;
      }
    },
  },
  beforeUnmount() {
    this.stopScanner();
  },
  methods: {
    closeDialog() {
      this.stopScanner();
      this.$emit("close");
    },

    async pasteFromClipboard() {
      try {
        const text = await navigator.clipboard.readText();
        if (text) {
          this.shareableId = text.trim();
          this.pasted = true;
          setTimeout(() => {
            this.pasted = false;
          }, 1500);
        }
      } catch (e) {
        console.error("[AddFriendDialog] Failed to paste:", e);
      }
    },

    async sendRequest() {
      if (!this.shareableId) {
        this.error =
          this.$t("chat.enter_shareable_id") || "Please enter shareable ID";
        return;
      }

      this.sending = true;
      this.error = null;
      this.success = false;

      try {
        const result = await ChatService.sendRequestViaShareableId(
          this.shareableId.trim(),
          this.message.trim() || null
        );

        if (!result.success) {
          // Map API error messages to human-readable translations
          const errorMessage = (result.message || "").toLowerCase();

          if (
            errorMessage.includes("yourself") ||
            errorMessage.includes("own")
          ) {
            this.error =
              this.$t("chat.cannot_send_to_yourself") ||
              "You cannot send a message request to yourself";
          } else if (
            errorMessage.includes("active conversation") ||
            errorMessage.includes("already friend")
          ) {
            this.error =
              this.$t("chat.already_have_conversation") ||
              "You already have an active conversation with this user";
          } else if (
            errorMessage.includes("request already") ||
            errorMessage.includes("pending") ||
            result.status === 409
          ) {
            this.error =
              this.$t("chat.request_already_exists") ||
              "A message request already exists between you and this user";
          } else if (
            errorMessage.includes("not found") ||
            errorMessage.includes("invalid") ||
            result.status === 404
          ) {
            this.error =
              this.$t("chat.user_not_found") ||
              "User not found. Please check the shareable ID";
          } else if (result.status === 400) {
            this.error =
              this.$t("chat.invalid_shareable_id") || "Invalid shareable ID";
          } else {
            this.error =
              result.message ||
              this.$t("chat.failed_to_send_request") ||
              "Failed to send request";
          }
          return;
        }

        this.success = true;
        this.$emit("request-sent", result.data);

        // Close dialog after a brief delay
        setTimeout(() => {
          this.closeDialog();
        }, 1500);
      } catch (e) {
        this.error =
          e?.message ||
          this.$t("chat.failed_to_send_request") ||
          "Failed to send request";
      } finally {
        this.sending = false;
      }
    },

    // QR Scanner methods (hidden by default, enable with ENABLE_QR_SCANNER flag)
    async openScanner() {
      if (!this.enableQrScanner) return;

      // Dynamically import html5-qrcode only when needed
      if (!Html5Qrcode) {
        try {
          const module = await import("html5-qrcode");
          Html5Qrcode = module.Html5Qrcode;
        } catch (e) {
          console.error("[AddFriendDialog] Failed to load QR scanner:", e);
          this.error =
            this.$t("chat.camera_access_denied") || "QR scanner not available";
          return;
        }
      }

      this.showScanner = true;
      await this.$nextTick();
      await this.startScanner();
    },

    closeScanner() {
      this.stopScanner();
      this.showScanner = false;
    },

    async startScanner() {
      try {
        const scannerElement = this.$refs.scannerElement;
        if (!scannerElement || !Html5Qrcode) return;

        // Add ID to element if missing
        if (!scannerElement.id) {
          scannerElement.id = "scanner-element-" + Date.now();
        }

        this.html5QrCode = new Html5Qrcode(scannerElement.id);

        await this.html5QrCode.start(
          { facingMode: "environment" },
          {
            fps: 10,
            qrbox: { width: 250, height: 250 },
          },
          (decodedText) => {
            this.onScanSuccess(decodedText);
          },
          () => {
            // Ignore scan errors (happens frequently while scanning)
          }
        );
      } catch (e) {
        console.error("[AddFriendDialog] Failed to start scanner:", e);
        this.error =
          this.$t("chat.camera_access_denied") || "Camera access denied";
        this.closeScanner();
      }
    },

    async stopScanner() {
      if (this.html5QrCode) {
        try {
          await this.html5QrCode.stop();
        } catch (e) {
          // Ignore stop errors
        }
        this.html5QrCode = null;
      }
    },

    onScanSuccess(decodedText) {
      this.stopScanner();
      this.showScanner = false;
      this.shareableId = decodedText;
    },
  },
};
</script>

<style scoped>
/* ========================================
   CLEAN WHITE ADD FRIEND DIALOG
   ======================================== */

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.spin {
  animation: spin 1s linear infinite;
}

/* Modal Overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(3px);
  animation: fadeIn 0.2s ease-out;
}

/* Main Dialog */
.add-friend-dialog {
  background: #ffffff;
  border-radius: 16px;
  width: 90%;
  max-width: 440px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  animation: slideUp 0.3s ease-out;
  overflow: hidden;
}

/* Header */
.dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(135deg, #00c444 0%, #00a838 100%);
  color: white;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
}

.header-icon {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-icon i {
  font-size: 1.3rem;
  color: #ffffff !important;
}

.dialog-header h2 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 600;
}

.close-btn {
  width: 34px;
  height: 34px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #ffffff !important;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.close-btn i {
  color: #ffffff !important;
  font-size: 0.9rem;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Content */
.dialog-content {
  padding: 28px 24px 24px;
}

/* Form Fields */
.form-field {
  margin-bottom: 20px;
}

.field-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 10px;
}

.optional-text {
  font-weight: 400;
  color: #9ca3af;
  font-size: 0.8rem;
}

/* Field Row - Input and Button Side by Side */
.field-row {
  display: flex;
  gap: 10px;
  align-items: stretch;
}

.field-input {
  flex: 1;
  padding: 13px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.95rem;
  background: #ffffff;
  transition: all 0.2s;
  font-family: inherit;
}

.field-input:focus {
  outline: none;
  border-color: #00c444;
  box-shadow: 0 0 0 3px rgba(0, 196, 68, 0.08);
}

.field-input::placeholder {
  color: #9ca3af;
}

.field-input:disabled {
  background: #f9fafb;
  cursor: not-allowed;
}

/* Paste Button */
.paste-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 0 18px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  background: #f9fafb;
  color: #00a838;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  white-space: nowrap;
}

.paste-btn:hover:not(:disabled) {
  background: #00c444;
  color: white;
  border-color: #00c444;
}

.paste-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.paste-btn i {
  font-size: 1rem;
}

/* Textarea */
.field-textarea {
  width: 100%;
  padding: 13px 16px;
  border: 2px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.95rem;
  resize: none;
  font-family: inherit;
  transition: all 0.2s;
  background: #ffffff;
}

.field-textarea:focus {
  outline: none;
  border-color: #00c444;
  box-shadow: 0 0 0 3px rgba(0, 196, 68, 0.08);
}

.field-textarea::placeholder {
  color: #9ca3af;
}

.field-textarea:disabled {
  background: #f9fafb;
  cursor: not-allowed;
}

/* Message Boxes */
.message-box {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 10px;
  font-size: 0.875rem;
  margin-bottom: 16px;
}

.message-box i {
  font-size: 1.1rem;
  flex-shrink: 0;
}

.error-box {
  background: #fef2f2;
  color: #dc2626;
}

.error-box i {
  color: #dc2626;
}

.success-box {
  background: #ecfdf5;
  color: #059669;
}

.success-box i {
  color: #00c444;
}

/* QR Scan Button */
.qr-scan-btn {
  width: 100%;
  padding: 13px;
  background: #f9fafb;
  border: 2px dashed #d1d5db;
  border-radius: 10px;
  color: #6b7280;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
}

.qr-scan-btn:hover:not(:disabled) {
  border-color: #00c444;
  color: #00a838;
  background: #f0fdf4;
  border-style: solid;
}

.qr-scan-btn i {
  font-size: 1.1rem;
}

/* Footer */
.dialog-footer {
  display: flex;
  gap: 12px;
  padding: 20px 24px 24px;
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
}

.btn-cancel {
  flex: 1;
  padding: 13px 20px;
  border: 2px solid #e5e7eb;
  background: #ffffff;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s;
}

.btn-cancel:hover:not(:disabled) {
  background: #f9fafb;
  border-color: #d1d5db;
}

.btn-cancel:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-send {
  flex: 1.5;
  padding: 13px 24px;
  border: none;
  border-radius: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  background: linear-gradient(135deg, #00c444 0%, #00a838 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.2s;
}

.btn-send i {
  color: white !important;
  font-size: 1rem;
}

.btn-send:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0, 196, 68, 0.25);
}

.btn-send:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  transform: none;
}

/* Scanner View */
.add-friend-dialog.scanner-mode {
  max-width: 400px;
  height: 500px;
  display: flex;
  flex-direction: column;
}

.scanner-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #00c444 0%, #00a838 100%);
  color: white;
}

.back-btn {
  width: 40px;
  height: 40px;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  color: #ffffff !important;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.back-btn i {
  color: #ffffff !important;
}

.back-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}

.scanner-header h2 {
  flex: 1;
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  text-align: center;
}

.spacer {
  width: 40px;
}

.scanner-container {
  flex: 1;
  position: relative;
  background: #1a1a2e;
  overflow: hidden;
}

.scanner-element {
  width: 100%;
  height: 100%;
}

.scanner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.scanner-frame {
  width: 250px;
  height: 250px;
  border: 3px solid #00c444;
  border-radius: 16px;
  box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.5);
}

.scanner-instruction {
  position: absolute;
  bottom: 40px;
  left: 0;
  right: 0;
  text-align: center;
  color: white;
  font-size: 0.95rem;
}

/* RTL Support */
[dir="rtl"] .dialog-header {
  flex-direction: row-reverse;
}

[dir="rtl"] .header-text {
  text-align: right;
}

[dir="rtl"] .input-with-action {
  flex-direction: row-reverse;
}

[dir="rtl"] .action-btn {
  border-left: none;
  border-right: 2px solid #e5e7eb;
}

[dir="rtl"] .error-message,
[dir="rtl"] .success-message {
  flex-direction: row-reverse;
}

[dir="rtl"] .scan-qr-btn {
  flex-direction: row-reverse;
}

[dir="rtl"] .back-btn i {
  transform: scaleX(-1);
}

/* Responsive */
@media (max-width: 480px) {
  .add-friend-dialog {
    width: 95%;
    border-radius: 16px;
  }

  .dialog-header {
    padding: 16px 20px;
  }

  .header-icon {
    width: 42px;
    height: 42px;
  }

  .dialog-header h2 {
    font-size: 1.1rem;
  }

  .dialog-content {
    padding: 20px;
  }

  .dialog-footer {
    padding: 16px 20px 20px;
  }

  .action-btn span {
    display: none;
  }

  .action-btn {
    padding: 0 14px;
  }
}
</style>
